<template>
	<div v-if="conversationExpirationTimestamp" class="expiration-container" :class="`status-${status}`">
        <InlineSvg
            class="icon"
            :src="clockStopwatchIcon"
        />
		<span class="txt">{{preText && status!=='expired' ? preText : ''}} {{ formattedTimeRemaining }}</span>

	</div>
</template>

<script setup>
import { loadFile } from '@/components/utils.js';
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import InlineSvg from '@/v2/components/inline-svg';
import {useI18n} from "vue-i18n";

const props = defineProps({
	conversationExpirationTimestamp: {
		type: String,
		required: false,
		default: null,
	},
	preText: {
		type: String,
		default: null,
	},
});

const { t, locale } = useI18n();

const conversationExpirationTimestamp = ref(props.conversationExpirationTimestamp);
const formattedTimeRemaining = ref('');
const status = ref('expired');
let timer = null;

const clockStopwatchIcon = loadFile(`whatsapp/clock.svg`);

const calculateExpiration = () => {
	if (conversationExpirationTimestamp.value) {
		const expirationTimestamp = parseInt(conversationExpirationTimestamp.value);
		const expirationDateTime = new Date(expirationTimestamp * 1000);
		const now = new Date();
		const difference = expirationDateTime - now;
		const hoursRemaining = Math.floor(difference / 3600000);
		const minutesRemaining = Math.floor((difference % 3600000) / 60000);

		const hourSymbol = locale.value === 'ar' ? 'س' : 'h';
		const minuteSymbol = locale.value === 'ar' ? 'د' : 'm';

		if (hoursRemaining <= 0 && minutesRemaining >= 0) {
			formattedTimeRemaining.value = `${minutesRemaining} ${minuteSymbol}`;
		} else if (hoursRemaining > 0 && minutesRemaining < 0) {
			formattedTimeRemaining.value = `${hoursRemaining} ${hourSymbol}`;
		} else if (hoursRemaining > 0 && minutesRemaining >= 0) {
			formattedTimeRemaining.value = `${hoursRemaining} ${hourSymbol} ${minutesRemaining} ${minuteSymbol}`;
		} else {
			formattedTimeRemaining.value = t('conversation_session_expired');
		}
		setClass(hoursRemaining, minutesRemaining);
	}
};

const setClass = (hoursRemaining, minutesRemaining) => {
	if (hoursRemaining >= 12) {
        status.value = 'blue';
	} else if (hoursRemaining >= 8) {
        status.value = 'orange';
    }
	else if (hoursRemaining > 0 || (hoursRemaining === 0 && minutesRemaining > 0)) {
        status.value = 'red';
    }
	else status.value = 'expired';
};

onMounted(() => {
	calculateExpiration();
	timer = setInterval(calculateExpiration, 60000);
});

onBeforeUnmount(() => {
	if (timer) {
		clearInterval(timer);
	}
});

watch(
	() => props.conversationExpirationTimestamp,
	(newTimestamp) => {
		conversationExpirationTimestamp.value = newTimestamp;
		calculateExpiration();
	},
);
</script>

<style scoped lang="scss">
.expiration-container {
	display: inline-flex;
	align-items: center;
	padding: 4px 6px;
	border-radius: 4px;
	border: 1px solid;
    font-size: 12px;
    font-weight: 500;
    gap: 5px;
    width: max-content;
    &.status-blue{
        border-color: #B9E6FE;
        background-color: #F5FBFF;
        color: #0086C9;
    }
    &.status-orange{
        border-color: #FEDF89;
        background-color: #FFFCF5;
        color: #DC6803;
    }
    &.status-red{
        border-color: #FECDCA;
        background-color: #FFFBFA;
        color: #DC341A;
    }
    &.status-expired{
        border-color: #D0D5DD;
        background-color: #FFFFFF;
        color: #667085;
    }
}
</style>
